import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { ReadField } from 'components/ReadField';
import type { ConstructionTypeData } from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { getOtherFormulaSourceObjectGql } from 'pages/Project/Common/Contract/contractQueries';
import type { TabsPropOnChange } from 'uikit';
import { cnMixSpace, Grid, GridItem, IconQuestion, Table, Tabs } from 'uikit';

import cn from 'classnames';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { InvisibleStyle, TAB_ITEMS } from './constants';
import { useOtherViewModal } from './hooks/useOtherViewModal';
import type { TabType } from './types';
import { TabId } from './types';

import Styles from './OtherViewModal.module.css';

interface IOtherViewModal {
    constructionTypes?: ConstructionTypeData[];
    contractId?: number;
    contractOtherCostId?: number;
    methodology: ProjectMethodologyEnum;
    onClose: () => void;
    workOrderOtherId?: number;
    workOrderChangeOtherId?: number;
    costRounding: number;
}

export const OtherViewModal: React.FC<IOtherViewModal> = (prop) => {
    const { methodology, onClose, costRounding, ...props } = prop;

    const { data, rows, columns, isLoading } = useOtherViewModal({
        methodology,
        costRounding,
        ...props,
    });

    const { deserializeFromBackend } = useEditorParseData({});
    const { control, setValue } = useForm();

    const [activeTab, setActiveTab] = useState<TabType>(TAB_ITEMS[0]);

    useEffect(() => {
        if (data.formula) {
            setValue('formula', deserializeFromBackend(data.formula));
        }
    }, [data.formula, deserializeFromBackend, setValue]);

    const tabItems = useMemo(
        () => (data?.isCalculatingMethodPercent ? TAB_ITEMS : R.init(TAB_ITEMS)),
        [data?.isCalculatingMethodPercent],
    );

    const handleTabChange = useCallback<TabsPropOnChange<TabType>>(({ value }) => setActiveTab(value), [setActiveTab]);

    const getTableAdditionalClassName = useCallback(() => Styles.tableRow, []);

    const labelRightSide = (
        <ComponentWithTooltip
            tooltipText="Расширение списка типа прочих доступно в справочнике переменных для формул прочих затрат"
            className={`${cnMixSpace({ mL: 'xs' })}`}
        >
            <IconQuestion view="secondary" />
        </ComponentWithTooltip>
    );

    const { isUer, isBim } = useMemo(
        () => ({
            isUer: methodology === ProjectMethodologyEnum.Uer,
            isBim: methodology === ProjectMethodologyEnum.Bim,
        }),
        [methodology],
    );

    return (
        <ModalDialog
            title="Просмотр прочих затрат"
            isOpen
            isLoading={isLoading}
            hasOverlay
            withTitleCloseButton
            isOnClickOutsideDisable
            onClose={onClose}
            btnCancelLabel="Закрыть"
            btnCancelView="primary"
        >
            <div
                className={cn(Styles.innerBlock, {
                    [Styles.innerBlock_bim]: isBim,
                    [Styles.innerBlock_uer]: isUer,
                })}
            >
                <Tabs className={Styles.tabs} value={activeTab} onChange={handleTabChange} items={tabItems} size="s" />
                <div className={Styles.tabsContent}>
                    <Grid style={activeTab.id === TabId.Common ? undefined : InvisibleStyle}>
                        <GridItem className={Styles.negativeM}>
                            <ReadField value={data.creationMethod} label="Способ создания" />
                        </GridItem>
                        <GridItem className={Styles.negativeM}>
                            <ReadField value={data.name} label="Наименование" />
                        </GridItem>
                        {isUer && (
                            <GridItem className={Styles.negativeM}>
                                <ReadField value={data.key} label="Код" />
                            </GridItem>
                        )}
                        <GridItem className={Styles.negativeM}>
                            <ReadField value={data.calculationMethod} label="Способ расчета" />
                        </GridItem>
                        {data.isCalculatingMethodPercent && (
                            <GridItem className={Styles.negativeM}>
                                <ReadField value={data.value} label="Величина" />
                            </GridItem>
                        )}
                        <GridItem className={Styles.negativeM}>
                            <ReadField value={data.chapter} label="Раздел" />
                        </GridItem>
                        <GridItem className={Styles.negativeM}>
                            <ReadField
                                value={data.otherCostTypeName}
                                label="Тип прочих"
                                labelRightSide={labelRightSide}
                            />
                        </GridItem>
                    </Grid>
                    <Grid style={activeTab.id === TabId.Limits ? undefined : InvisibleStyle}>
                        <Table
                            className={cn(Styles.table, {
                                [Styles.table_bim]: methodology === ProjectMethodologyEnum.Bim,
                                [Styles.table_uer]: methodology === ProjectMethodologyEnum.Uer,
                            })}
                            columns={columns}
                            rows={rows}
                            getAdditionalClassName={getTableAdditionalClassName}
                            verticalAlign="center"
                        />
                    </Grid>
                    <Grid style={activeTab.id === TabId.Formula ? undefined : InvisibleStyle}>
                        {data.isCalculatingMethodPercent && (
                            <FormulaEditor
                                name="formula"
                                control={control}
                                getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                                tooltipTitle="Формула расчета"
                                tooltipTitleClassName={Styles.formulaTitle}
                                tooltipMessage=""
                                placeholderMessage=""
                                isReadOnly
                            />
                        )}
                    </Grid>
                </div>
            </div>
        </ModalDialog>
    );
};

OtherViewModal.displayName = 'OtherViewModal';
